import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import './HRAttendanceReport.css';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

function formatTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('en-PK', {
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function formatHours(minutes) {
  if (!minutes) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

function getInitials(name) {
  if (!name) return '?';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length - 1][0] || '')).toUpperCase();
}

function getAvatarColor(name) {
  const colors = ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#ef4444','#06b6d4','#f97316'];
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h += name.charCodeAt(i);
  return colors[h % colors.length];
}

function getRowStyle(record) {
  if (record.status === 'weekly_off') return 'ar-row-weekoff';
  if (record.status === 'public_holiday') return 'ar-row-holiday';
  if (record.status === 'approved_leave' || record.status === 'casual_auto') return 'ar-row-leave';
  if (!record.clock_in) return 'ar-row-absent';
  if (record.late_minutes > 0) return 'ar-row-late';
  return 'ar-row-present';
}

function getStatusBadge(record) {
  if (record.status === 'weekly_off')     return { label: '🌙 Weekly Off', cls: 'ar-badge-weekoff' };
  if (record.status === 'public_holiday') return { label: '🎉 Holiday',  cls: 'ar-badge-holiday' };
  if (record.status === 'approved_leave') return { label: '📋 On Leave', cls: 'ar-badge-leave' };
  if (record.status === 'casual_auto')    return { label: '📋 Casual',   cls: 'ar-badge-leave' };
  if (!record.clock_in)                  return { label: '✗ Absent',    cls: 'ar-badge-absent' };
  if (record.late_minutes > 0)           return { label: `⏱ Late`,      cls: 'ar-badge-late' };
  return                                        { label: '✓ Present',   cls: 'ar-badge-present' };
}

export default function HRAttendanceReport() {
  const [employees, setEmployees]   = useState([]);
  const [search, setSearch]         = useState('');
  const [selected, setSelected]     = useState(null);
  const [report, setReport]         = useState(null);
  const [loading, setLoading]       = useState(false);
  const [empLoading, setEmpLoading] = useState(true);
  const [month, setMonth]           = useState(new Date().getMonth() + 1);
  const [year, setYear]             = useState(new Date().getFullYear());

  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Load all employees once
  useEffect(() => {
    api.getAllEmployees().then(res => {
      setEmployees(res.employees || []);
      setEmpLoading(false);
    });
  }, []);

  // Load report when employee or month/year changes
  useEffect(() => {
    if (!selected) return;
    loadReport(selected.id);
  }, [selected, month, year]);

  const loadReport = async (empId) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `https://fotileams.com/api/attendance/employee/${empId}?month=${month}&year=${year}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to load attendance data');
        setReport(null);
      } else {
        setReport(data);
      }
    } catch (err) {
      console.error(err);
      setError('Network error — please try again');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const filtered = employees.filter(e => {
    const q = search.toLowerCase();
    return (
      e.full_name?.toLowerCase().includes(q) ||
      e.employee_id?.toLowerCase().includes(q) ||
      e.department?.toLowerCase().includes(q)
    );
  });

  const years = [];
  for (let y = new Date().getFullYear(); y >= 2024; y--) years.push(y);

  const totalHours = report ? formatHours(report.summary?.total_minutes) : '—';

  const downloadExcelReport = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `https://fotileams.com/api/reports/excel?month=${month}&year=${year}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to generate report');
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `Fotile_AMS_Report_${MONTHS[month-1]}_${year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="ar-root">

      {/* LEFT PANEL — Employee List */}
      <div className="ar-left">
        <div className="ar-left-header">
          <div className="ar-left-title">Employees</div>
          <div className="ar-left-count">{employees.length}</div>
        </div>
        <div className="ar-report-period">
          <span className="ar-report-period-label">Report Period</span>
          <div className="ar-report-period-row">
            <select className="ar-period-select" value={month} onChange={e => setMonth(parseInt(e.target.value))}>
              {MONTHS.map((m, i) => (
                <option key={i} value={i + 1}>{m}</option>
              ))}
            </select>
            <select className="ar-period-select" value={year} onChange={e => setYear(parseInt(e.target.value))}>
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        <button className="ar-download-btn" onClick={downloadExcelReport} disabled={downloading}>
          {downloading ? '⏳ Generating…' : `📥 Download ${MONTHS[month-1]} ${year} Report`}
        </button>
        <div className="ar-search-wrap">
          <span className="ar-search-icon">🔍</span>
          <input
            className="ar-search"
            placeholder="Search name, ID, dept…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {empLoading ? (
          <div className="ar-emp-loading">Loading…</div>
        ) : (
          <div className="ar-emp-list">
            {filtered.map((emp, i) => {
              const isActive = selected?.id === emp.id;
              const color    = getAvatarColor(emp.full_name);
              return (
                <div key={i}
                  className={`ar-emp-item ${isActive ? 'ar-emp-active' : ''}`}
                  onClick={() => setSelected(emp)}
                >
                  <div className="ar-emp-av" style={{ background: color }}>
                    {getInitials(emp.full_name)}
                  </div>
                  <div className="ar-emp-info">
                    <div className="ar-emp-name">{emp.full_name}</div>
                    <div className="ar-emp-meta">{emp.employee_id} · {emp.department || 'Unassigned'}</div>
                  </div>
                  {isActive && <span className="ar-emp-arrow">›</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* RIGHT PANEL — Report */}
      <div className="ar-right">
        {!selected ? (
          <div className="ar-empty-state">
            <div className="ar-empty-icon">👆</div>
            <div className="ar-empty-title">Select an Employee</div>
            <div className="ar-empty-sub">Click any employee on the left to view their detailed attendance report</div>
          </div>
        ) : (
          <>
            {/* Employee header */}
            <div className="ar-report-header">
              <div className="ar-report-emp-row">
                <div className="ar-report-av" style={{ background: getAvatarColor(selected.full_name) }}>
                  {getInitials(selected.full_name)}
                </div>
                <div className="ar-report-emp-info">
                  <div className="ar-report-emp-name">{selected.full_name}</div>
                  <div className="ar-report-emp-meta">
                    {selected.employee_id} · {selected.department || 'Unassigned'} · {selected.city}
                  </div>
                </div>
              </div>

              {/* Month/Year selector */}
              <div className="ar-period-row">
                <div className="ar-month-pills">
                  {MONTHS.map((m, i) => (
                    <button key={i}
                      className={`ar-pill ${month === i + 1 ? 'ar-pill-active' : ''}`}
                      onClick={() => setMonth(i + 1)}>
                      {m.slice(0, 3)}
                    </button>
                  ))}
                </div>
                <div className="ar-year-pills">
                  {years.map(y => (
                    <button key={y}
                      className={`ar-pill ${year === y ? 'ar-pill-active' : ''}`}
                      onClick={() => setYear(y)}>
                      {y}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {loading ? (
              <div className="ar-loading"><div className="ar-spinner" /></div>
            ) : error ? (
              <div className="ar-error-state">
                <div className="ar-error-icon">⚠️</div>
                <div className="ar-error-title">Failed to load data</div>
                <div className="ar-error-sub">{error}</div>
                <button className="ar-retry-btn" onClick={() => loadReport(selected.id)}>Retry</button>
              </div>
            ) : report ? (
              <>
                {/* Summary KPI strip */}
                <div className="ar-kpi-strip">
                  <div className="ar-kpi ar-kpi-green">
                    <div className="ar-kpi-num">{report.summary?.present ?? 0}</div>
                    <div className="ar-kpi-lbl">Present</div>
                  </div>
                  <div className="ar-kpi ar-kpi-amber">
                    <div className="ar-kpi-num">{report.summary?.late ?? 0}</div>
                    <div className="ar-kpi-lbl">Late</div>
                  </div>
                  <div className="ar-kpi ar-kpi-red">
                    <div className="ar-kpi-num">{report.summary?.absent ?? 0}</div>
                    <div className="ar-kpi-lbl">Absent</div>
                  </div>
                  <div className="ar-kpi ar-kpi-purple">
                    <div className="ar-kpi-num">{report.summary?.holidays ?? 0}</div>
                    <div className="ar-kpi-lbl">Holidays</div>
                  </div>
                  <div className="ar-kpi ar-kpi-navy">
                    <div className="ar-kpi-num ar-kpi-num-sm">{totalHours}</div>
                    <div className="ar-kpi-lbl">Total Hours</div>
                  </div>
                </div>

                {/* Attendance table */}
                {report.records.length === 0 ? (
                  <div className="ar-no-records">
                    No records for {MONTHS[month - 1]} {year}
                  </div>
                ) : (
                  <div className="ar-table-wrap">
                    <table className="ar-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Day</th>
                          <th>Clock In</th>
                          <th>Clock Out</th>
                          <th>Hours</th>
                          <th>Late</th>
                          <th>Status</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.records.map((r, i) => {
                          const rowCls  = getRowStyle(r);
                          const badge   = getStatusBadge(r);
                          const dateObj = new Date(r.date);
                          const day     = dateObj.toLocaleDateString('en-PK', { weekday: 'short' });
                          const dateStr = dateObj.toLocaleDateString('en-PK', { day: 'numeric', month: 'short' });
                          return (
                            <tr key={i} className={rowCls}>
                              <td className="ar-td-date">{dateStr}</td>
                              <td className="ar-td-day">{day}</td>
                              <td className="ar-td-time">{formatTime(r.clock_in)}</td>
                              <td className="ar-td-time">{r.clock_in && !r.clock_out ? <span className="ar-live">● Live</span> : formatTime(r.clock_out)}</td>
                              <td className="ar-td-hours">{formatHours(r.total_minutes)}</td>
                              <td className="ar-td-late">
                                {r.late_minutes > 0
                                  ? <span className="ar-late-pill">+{r.late_minutes}m</span>
                                  : '—'}
                              </td>
                              <td className="ar-td-status">
                                <span className={`ar-badge ${badge.cls}`}>{badge.label}</span>
                              </td>
                              <td className="ar-td-notes">{r.notes || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}